import { z } from 'zod';

export type StreamId = z.infer<typeof StreamId>;
export const StreamId = z.string();

export type SearchFilter = z.infer<typeof SearchFilter>;
export const SearchFilter = z.object({
    type: z.union([z.literal('account_id'), z.literal('asset_id'), z.literal('device_type')]),
    query: z.string(),
});

export type Stream = z.infer<typeof Stream>;
export const Stream = z.object({
    id: StreamId,
    filter: SearchFilter,
    started_at: z.union([z.string(), z.undefined()]).optional(),
    ended_at: z.union([z.string(), z.null(), z.undefined()]).optional(),
    number_of_events: z.union([z.number(), z.undefined()]).optional(),
});

export type StreamList = z.infer<typeof StreamList>;
export const StreamList = z.object({
    items: z.array(Stream),
});

export type TagType = z.infer<typeof TagType>;
export const TagType = z.union([z.literal('data'), z.literal('warning'), z.literal('all')]);

export type Tag = z.infer<typeof Tag>;
export const Tag = z.object({
    name: z.string(),
    type: TagType,
});

export type Event = z.infer<typeof Event>;
export const Event = z.object({
    id: z.string(),
    occurred_at: z.string(),
    sent_to_kafka_at: z.union([z.string(), z.undefined()]).optional(),
    asset_id: z.string(),
    account_id: z.string(),
    eid: z.string(),
    device_type: z.union([z.string(), z.undefined()]).optional(),
    trigger: z.string(),
    tags: z.array(Tag),
});

export type Link = z.infer<typeof Link>;
export const Link = z.object({
    href: z.string(),
});

export type PaginationLinks = z.infer<typeof PaginationLinks>;
export const PaginationLinks = z.object({
    self: Link,
    next: Link,
});

export type EventsList = z.infer<typeof EventsList>;
export const EventsList = z.object({
    items: z.array(Event),
    _links: PaginationLinks,
});

export type TagStatistic = z.infer<typeof TagStatistic>;
export const TagStatistic = z.object({
    tag: Tag,
    count: z.number(),
    percentage: z.number(),
    inter_event_time_p90: z.union([z.string(), z.undefined()]).optional(),
    latency_p90: z.union([z.string(), z.undefined()]).optional(),
});

export type Statistics = z.infer<typeof Statistics>;
export const Statistics = z.object({
    tag_statistics: z.array(TagStatistic),
});

export type MonitorId = z.infer<typeof MonitorId>;
export const MonitorId = z.string();

export type Monitor = z.infer<typeof Monitor>;
export const Monitor = z.object({
    id: MonitorId,
    description: z.string(),
    search_filter: SearchFilter,
});

export type MonitorList = z.infer<typeof MonitorList>;
export const MonitorList = z.object({
    items: z.array(Monitor),
});

export type TagStatisticTimeseriesPoint = z.infer<typeof TagStatisticTimeseriesPoint>;
export const TagStatisticTimeseriesPoint = z.object({
    timestamp: z.string(),
    value: z.number(),
    standardError: z.union([z.number(), z.undefined()]).optional(),
});

export type TagStatisticTimeseries = z.infer<typeof TagStatisticTimeseries>;
export const TagStatisticTimeseries = z.object({
    tag: Tag,
    label: z.string(),
    unit: z.union([z.string(), z.undefined()]).optional(),
    timeseries: z.array(TagStatisticTimeseriesPoint),
});

export type MonitorStatisticTimeseries = z.infer<typeof MonitorStatisticTimeseries>;
export const MonitorStatisticTimeseries = z.object({
    id: z.string(),
    timeseries: z.array(TagStatisticTimeseries),
});

export type put_CreateStream = typeof put_CreateStream;
export const put_CreateStream = {
    method: z.literal('PUT'),
    path: z.literal('/streams/{streamId}'),
    requestFormat: z.literal('json'),
    parameters: z.object({
        path: z.object({
            streamId: z.string(),
        }),
        body: Stream,
    }),
    response: z.unknown(),
};

export type get_GetStreams = typeof get_GetStreams;
export const get_GetStreams = {
    method: z.literal('GET'),
    path: z.literal('/streams'),
    requestFormat: z.literal('json'),
    parameters: z.never(),
    response: StreamList,
};

export type get_GetEvents = typeof get_GetEvents;
export const get_GetEvents = {
    method: z.literal('GET'),
    path: z.literal('/streams/{streamId}/events'),
    requestFormat: z.literal('json'),
    parameters: z.object({
        query: z.object({
            after: z.string().optional(),
        }),
        path: z.object({
            streamId: z.string(),
        }),
    }),
    response: EventsList,
};

export type get_GetStatistics = typeof get_GetStatistics;
export const get_GetStatistics = {
    method: z.literal('GET'),
    path: z.literal('/streams/{streamId}/statistics'),
    requestFormat: z.literal('json'),
    parameters: z.object({
        path: z.object({
            streamId: z.string(),
        }),
    }),
    response: Statistics,
};

export type get_GetMonitors = typeof get_GetMonitors;
export const get_GetMonitors = {
    method: z.literal('GET'),
    path: z.literal('/monitors'),
    requestFormat: z.literal('json'),
    parameters: z.never(),
    response: MonitorList,
};

export type get_GetMonitorTimeseries = typeof get_GetMonitorTimeseries;
export const get_GetMonitorTimeseries = {
    method: z.literal('GET'),
    path: z.literal('/monitors/{monitorId}/timeseries'),
    requestFormat: z.literal('json'),
    parameters: z.object({
        path: z.object({
            monitorId: z.string(),
        }),
    }),
    response: MonitorStatisticTimeseries,
};

// <EndpointByMethod>
export const EndpointByMethod = {
    put: {
        '/streams/{streamId}': put_CreateStream,
    },
    get: {
        '/streams': get_GetStreams,
        '/streams/{streamId}/events': get_GetEvents,
        '/streams/{streamId}/statistics': get_GetStatistics,
        '/monitors': get_GetMonitors,
        '/monitors/{monitorId}/timeseries': get_GetMonitorTimeseries,
    },
};
export type EndpointByMethod = typeof EndpointByMethod;
// </EndpointByMethod>

// <EndpointByMethod.Shorthands>
export type PutEndpoints = EndpointByMethod['put'];
export type GetEndpoints = EndpointByMethod['get'];
export type AllEndpoints = EndpointByMethod[keyof EndpointByMethod];
// </EndpointByMethod.Shorthands>

// <ApiClientTypes>
export type EndpointParameters = {
    body?: unknown;
    query?: Record<string, unknown>;
    header?: Record<string, unknown>;
    path?: Record<string, unknown>;
};

export type MutationMethod = 'post' | 'put' | 'patch' | 'delete';
export type Method = 'get' | 'head' | 'options' | MutationMethod;

type RequestFormat = 'json' | 'form-data' | 'form-url' | 'binary' | 'text';

export type DefaultEndpoint = {
    parameters?: EndpointParameters | undefined;
    response: unknown;
};

export type Endpoint<TConfig extends DefaultEndpoint = DefaultEndpoint> = {
    operationId: string;
    method: Method;
    path: string;
    requestFormat: RequestFormat;
    parameters?: TConfig['parameters'];
    meta: {
        alias: string;
        hasParameters: boolean;
        areParametersRequired: boolean;
    };
    response: TConfig['response'];
};

type Fetcher = (
    method: Method,
    url: string,
    parameters?: EndpointParameters | undefined
) => Promise<Endpoint['response']>;

type RequiredKeys<T> = {
    [P in keyof T]-?: undefined extends T[P] ? never : P;
}[keyof T];

type MaybeOptionalArg<T> = RequiredKeys<T> extends never ? [config?: T] : [config: T];

// </ApiClientTypes>

// <ApiClient>
export class ApiClient {
    baseUrl = '';

    constructor(public fetcher: Fetcher) {}

    setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
        return this;
    }

    // <ApiClient.put>
    put<Path extends keyof PutEndpoints, TEndpoint extends PutEndpoints[Path]>(
        path: Path,
        ...params: MaybeOptionalArg<z.infer<TEndpoint['parameters']>>
    ): Promise<z.infer<TEndpoint['response']>> {
        return this.fetcher('put', this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint['response']>>;
    }
    // </ApiClient.put>

    // <ApiClient.get>
    get<Path extends keyof GetEndpoints, TEndpoint extends GetEndpoints[Path]>(
        path: Path,
        ...params: MaybeOptionalArg<z.infer<TEndpoint['parameters']>>
    ): Promise<z.infer<TEndpoint['response']>> {
        return this.fetcher('get', this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint['response']>>;
    }
    // </ApiClient.get>
}

export function createApiClient(fetcher: Fetcher, baseUrl?: string) {
    return new ApiClient(fetcher).setBaseUrl(baseUrl ?? '');
}

/**
 Example usage:
 const api = createApiClient((method, url, params) =>
   fetch(url, { method, body: JSON.stringify(params) }).then((res) => res.json()),
 );
 api.get("/users").then((users) => console.log(users));
 api.post("/users", { body: { name: "John" } }).then((user) => console.log(user));
 api.put("/users/:id", { path: { id: 1 }, body: { name: "John" } }).then((user) => console.log(user));
*/

// </ApiClient
